/**
 * FlahaSOIL v2 application shell.
 *
 * Browser routes remain authoritative. The sidebar is the complete navigation
 * library, while WorkspaceDock is an authenticated quick-workflow launcher.
 */
import { Box, Drawer, Toolbar, useMediaQuery, useTheme } from "@mui/material";
import { useState } from "react";
import { Outlet } from "react-router-dom";

import { useAuth } from "../auth";
import { flahaSoilColors } from "../theme/flahaSoilTheme";
import { FlahaLogo } from "./components/FlahaLogo";
import { GlassSurface } from "./components/GlassSurface";
import { PageContextBar } from "./components/PageContextBar";
import { SidebarNav } from "./components/SidebarNav";
import { TopAppBar } from "./components/TopAppBar";
import { WorkspaceDock } from "./components/WorkspaceDock";

const DRAWER_WIDTH = 264;
const MAIN_CONTENT_ID = "main-content";

export function AppLayout() {
	const theme = useTheme();
	const isDesktop = useMediaQuery(theme.breakpoints.up("md"));
	const [mobileOpen, setMobileOpen] = useState(false);
	const { status } = useAuth();
	const showAuthenticatedShell = status === "authenticated";
	const effectiveDrawerWidth = showAuthenticatedShell ? DRAWER_WIDTH : 0;

	const closeMobileNavigation = () => setMobileOpen(false);

	const drawerContent = (
		<GlassSurface
			intensity="strong"
			depth="flat"
			sx={{
				display: "flex",
				flexDirection: "column",
				height: "100%",
				border: 0,
				borderRight: (currentTheme) =>
					`1px solid ${currentTheme.palette.workspace.border}`,
				borderRadius: 0,
				backgroundImage:
					"linear-gradient(180deg, rgba(119,169,87,0.07), transparent 28%)",
			}}
		>
			<Toolbar
				sx={{
					minHeight: 72,
					px: 2,
					overflow: "hidden",
					borderBottom: (currentTheme) =>
						`1px solid ${currentTheme.palette.workspace.border}`,
				}}
			>
				{/* Single product mark + wordmark — matches top bar lockup */}
				<FlahaLogo size={40} variant="full" color="#F6F0E3" />
			</Toolbar>
			<SidebarNav onNavigate={closeMobileNavigation} />
		</GlassSurface>
	);

	return (
		<Box
			sx={{
				display: "flex",
				minHeight: "100vh",
				position: "relative",
				color: "workspace.textPrimary",
			}}
		>
				<Box
					component="a"
					href={`#${MAIN_CONTENT_ID}`}
					sx={{
						position: "fixed",
						top: 8,
						left: 8,
						zIndex: theme.zIndex.modal + 1,
						px: 2,
						py: 1,
						borderRadius: 1,
						color: flahaSoilColors.workspaceCream,
						backgroundColor: flahaSoilColors.blue700,
						textDecoration: "none",
						transform: "translateY(-160%)",
						transition: theme.motion.transition.focus,
						"&:focus": {
							transform: "translateY(0)",
						},
					}}
				>
					Skip to main content
				</Box>

				<TopAppBar
					drawerWidth={effectiveDrawerWidth}
					showMenuButton={showAuthenticatedShell}
					mobileMenuOpen={mobileOpen}
					onMenuClick={() => setMobileOpen((current) => !current)}
				/>

				{showAuthenticatedShell && (
					<Box
						sx={{
							width: { md: DRAWER_WIDTH },
							flexShrink: { md: 0 },
						}}
					>
						{isDesktop ? (
							<Drawer
								variant="permanent"
								open
								PaperProps={{
									sx: {
										width: DRAWER_WIDTH,
										boxSizing: "border-box",
										backgroundColor: "transparent",
										border: 0,
									},
								}}
							>
								{drawerContent}
							</Drawer>
						) : (
							<Drawer
								id="mobile-navigation"
								variant="temporary"
								open={mobileOpen}
								onClose={closeMobileNavigation}
								ModalProps={{ keepMounted: true }}
								PaperProps={{
									sx: {
										width: `min(${DRAWER_WIDTH}px, 88vw)`,
										boxSizing: "border-box",
										backgroundColor: "transparent",
										border: 0,
									},
								}}
							>
								{drawerContent}
							</Drawer>
						)}
					</Box>
				)}

				<Box
					sx={{
						flexGrow: 1,
						width: { md: `calc(100% - ${effectiveDrawerWidth}px)` },
						minWidth: 0,
						minHeight: "100vh",
						display: "flex",
						flexDirection: "column",
					}}
				>
					<Toolbar sx={{ minHeight: 72 }} />
					<PageContextBar />
					<Box
						id={MAIN_CONTENT_ID}
						component="main"
						tabIndex={-1}
						sx={{
							flexGrow: 1,
							minWidth: 0,
							m: { xs: 0, sm: 1.5, lg: 2.5 },
							mt: { xs: 0, sm: 1.5 },
							// Extra bottom room for the fixed mobile dock + safe area
							mb: showAuthenticatedShell
								? { xs: 11.5, sm: 12, md: 13 }
								: { xs: 0, sm: 1.5 },
							p: { xs: 1.75, sm: 3, lg: 4 },
							borderRadius: { xs: 0, sm: 2.5 },
							color: "workspace.textPrimary",
							// Dark glass main surface — never light cream on dark chrome
							backgroundColor: "rgba(18, 24, 21, 0.82)",
							backgroundImage:
								"linear-gradient(160deg, rgba(119,169,87,0.04), transparent 42%), linear-gradient(320deg, rgba(74,156,197,0.04), transparent 48%)",
							border: {
								xs: "none",
								sm: `1px solid ${theme.palette.workspace.border}`,
							},
							boxShadow: {
								xs: "none",
								sm: "0 24px 70px rgba(0, 0, 0, 0.32)",
							},
							"&:focus": { outline: "none" },
							"&:focus-visible": {
								outline: `3px solid ${theme.palette.workspace.blue}`,
								outlineOffset: -3,
							},
						}}
					>
						<Outlet />
					</Box>
				</Box>

				{showAuthenticatedShell && (
					<WorkspaceDock
						desktopOffset={DRAWER_WIDTH / 2}
						navigationOpen={mobileOpen}
						onOpenNavigation={() => setMobileOpen(true)}
					/>
				)}
			</Box>
	);
}
